import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { makeStyles } from '@material-ui/core';
import {
  CapaRfqPcbAssemblyMethodType,
  CapaPcbServiceType,
  RegisterRfqPcbInput
} from '@generated/graphql';
import useRequest from '@hooks/useRequest';
import useQueryString from '@hooks/useQueryString';
import { RequestQueryString } from '@routes/NewRequest/types';
import PCBStepProps from './types';
import {
  CircuitAssemblyFileInput,
  CircuitAssemblyMaterialSupplyMethodControl,
  CircuitAssemblyMetalMaskAvailablityControl,
  CircuitAssemblyMethodControl,
  CircuitAssemblyQuantityInput
} from '@routes/NewRequest/FormComponents';
import { CustomizedTypography } from '@components/Customized';

interface AssembleRequestForm {
  pcb: Pick<
    RegisterRfqPcbInput,
    | 'assemblyFiles'
    | 'assemblyQuantity'
    | 'assemblyMethod'
    | 'metalMaskAvailablity'
    | 'materialSupplyMethod'
  >;
}

const PCBAssembleStep: React.FC<PCBStepProps> = ({
  setDisabledNextButton,
  nextButtonAction
}) => {
  const [isValid, setIsValid] = useState<boolean>(false);
  const { requestState, onUpdateRfqInfo } = useRequest();
  const { requestContainer } = usePCBRequestStyles();
  const {
    query: { process }
  } = useQueryString<RequestQueryString>();
  const processes = process?.split(',');
  const hasCircuitDesign = processes?.includes(
    CapaPcbServiceType.CircuitDesign
  );
  const formProps = useForm<AssembleRequestForm>({
    mode: 'onBlur',
    defaultValues: {
      pcb: {
        assemblyMethod: requestState.pcb?.assemblyMethod ?? [],
        assemblyQuantity: requestState.pcb?.assemblyQuantity,
        materialSupplyMethod: requestState.pcb?.materialSupplyMethod,
        metalMaskAvailablity: requestState.pcb?.metalMaskAvailablity
      }
    }
  });
  const { errors, watch } = formProps;
  const { pcb } = errors;
  const watchAssembleForm = watch();
  const quantity = watchAssembleForm.pcb?.assemblyQuantity;
  const assemblyMethod: CapaRfqPcbAssemblyMethodType[] = (
    watchAssembleForm.pcb?.assemblyMethod ?? []
  ).map((method) => method.methodType);
  const currentFormValue = useRef<AssembleRequestForm>({
    pcb: {
      assemblyMethod: requestState.pcb?.assemblyMethod ?? [],
      assemblyQuantity: requestState.pcb?.assemblyQuantity,
      materialSupplyMethod: requestState.pcb?.materialSupplyMethod,
      metalMaskAvailablity: requestState.pcb?.metalMaskAvailablity
    }
  });
  const assemblyFilesRef = useRef<any[]>([]);

  const onSubmit = useCallback(() => {
    const { pcb } = currentFormValue.current;
    const {
      assemblyMethod,
      assemblyQuantity,
      materialSupplyMethod,
      metalMaskAvailablity
    } = pcb ?? {};

    onUpdateRfqInfo({
      pcb: {
        ...requestState.pcb,
        services:
          requestState?.pcb?.services ??
          processes?.map((service) => ({
            serviceType: service as CapaPcbServiceType
          })) ??
          [],
        assemblyFiles: assemblyFilesRef.current,
        assemblyQuantity,
        assemblyMethod,
        materialSupplyMethod,
        metalMaskAvailablity
      }
    });
  }, [requestState, processes, onUpdateRfqInfo]);

  useEffect(() => {
    const { pcb } = watchAssembleForm;
    const {
      assemblyMethod,
      assemblyQuantity,
      materialSupplyMethod,
      metalMaskAvailablity
    } = pcb ?? {};

    currentFormValue.current = {
      pcb: {
        assemblyQuantity,
        assemblyMethod,
        materialSupplyMethod,
        metalMaskAvailablity
      }
    };
  }, [watchAssembleForm]);

  useEffect(() => {
    nextButtonAction.current.ASSEMBLY = onSubmit;
  }, [onSubmit, nextButtonAction]);

  useEffect(() => {
    if (!hasCircuitDesign) {
      if (
        (requestState?.pcb?.assemblyFiles ?? []).length > 0 &&
        requestState?.pcb?.assemblyFiles?.every((file) => {
          return file.fileId && file.fileName && file.quantity;
        }) &&
        (assemblyMethod?.length ?? 0) > 0
      ) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    }

    if (hasCircuitDesign) {
      if (quantity) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    }
  }, [requestState, hasCircuitDesign, quantity, assemblyMethod]);

  useEffect(() => {
    if (!setDisabledNextButton) return;

    setDisabledNextButton(!isValid);
  }, [isValid, setDisabledNextButton]);

  useEffect(() => {
    return onSubmit;

    //eslint-disable-next-line
  }, []);

  return (
    <div className={requestContainer}>
      <FormProvider {...formProps}>
        <form>
          {!hasCircuitDesign && (
            <>
              <CircuitAssemblyFileInput filesRef={assemblyFilesRef} />
              <CircuitAssemblyMethodControl
                error={pcb?.['assemblyMethod']?.message}
              />
              <CircuitAssemblyMetalMaskAvailablityControl
                error={pcb?.['metalMaskAvailablity']?.message}
              />
              <CircuitAssemblyMaterialSupplyMethodControl
                error={pcb?.['materialSupplyMethod']?.message}
              />
            </>
          )}
          {hasCircuitDesign && (
            <>
              <CircuitAssemblyQuantityInput
                error={pcb?.['assemblyQuantity']?.message}
              />
              <div className="bom-description">
                <CustomizedTypography
                  variant="body1"
                  fontWeight="500"
                  color="textPrimary"
                  gutterBottoms={8}
                >
                  유의사항
                </CustomizedTypography>
                <CustomizedTypography variant="body2" color="textSecondary">
                  PCB가 오류를 일으키지 않고 제대로 작동하기 위해선 각각의
                  부품이 결함 없이 정교하게 조립되어야 합니다. 그러기 위해선
                  작업에 앞서 전문업체의 장비 수준과 내부 직원의 숙련도를
                  체크하는 것이 중요합니다. 업체를 선정했다면 공정 단계에서
                  오류가 발생하지 않도록 어떤 준비가 되어있는지 해당 업체로부터
                  확인을 받는 것이 좋습니다.
                </CustomizedTypography>
              </div>
            </>
          )}
        </form>
      </FormProvider>
    </div>
  );
};

export default PCBAssembleStep;

const usePCBRequestStyles = makeStyles((theme) => ({
  requestContainer: {
    width: '100%',
    maxWidth: '464px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '72px',
    '& .bom-description': {
      marginTop: 40,
      '& > p': {
        wordBreak: 'break-all'
      }
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0
    }
  }
}));
